// SPDX-License-Identifier: AGPL-3.0-or-later
// @ts-nocheck
// Profile card effects, ported from the approved design preview. One engine per mounted card.

export const PROFILE_FX_KEYS = ['autumn', 'winter', 'fire', 'sakura', 'aurora', 'rain', 'galaxy', 'neon', 'storm', 'fireflies'];

/** Soft colour wash drawn under the effect for each theme (matches the preview). */
export const PROFILE_FX_TINTS = {
  autumn: {background: 'radial-gradient(260px 200px at 0% 0%, rgba(255,170,60,.22), transparent 70%), radial-gradient(220px 160px at 100% 100%, rgba(200,70,20,.16), transparent 70%)', mixBlendMode: 'soft-light'},
  winter: {background: 'linear-gradient(180deg, rgba(170,205,255,.10), rgba(120,170,255,.06))'},
  sakura: {background: 'linear-gradient(200deg, rgba(255,190,215,.14), transparent 60%)'},
  aurora: {background: 'linear-gradient(180deg, rgba(5,15,35,.55), transparent 60%)'},
  rain: {background: 'linear-gradient(180deg, rgba(90,120,160,.16), rgba(40,60,90,.12))'},
  galaxy: {background: 'rgba(10,5,30,.14)'},
  fireflies: {background: 'linear-gradient(180deg, rgba(20,30,70,.28), rgba(10,40,35,.1))'},
};

export function createProfileFx(cardEl, canvasEl, glCanvasEl) {

  const card = cardEl, canvas = canvasEl, glCanvas = glCanvasEl;
  const ctx = canvas.getContext('2d');
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  let W = 0, H = 0, DPR = 1;
  // Only fire/aurora use WebGL. Track the context so dispose() can release it: browsers keep
  // lost-but-unreleased contexts (and their GPU/CPU buffers) alive until GC.
  let glContext = null;
  function getGL() {
    if (!glContext) glContext = glCanvas.getContext('webgl', {premultipliedAlpha: true, alpha: true, antialias: false});
    return glContext;
  }

  function resize() {
    const r = card.getBoundingClientRect();
    // Not laid out yet (hidden/collapsed): effects allocate canvases from W/H, so wait for a real size.
    if (r.width < 2 || r.height < 2) return;
    DPR = Math.min(2, window.devicePixelRatio || 1);
    W = r.width; H = r.height;
    for (const c of [canvas, glCanvas]) { c.width = Math.round(W * DPR); c.height = Math.round(H * DPR); }
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    if (pending) setEffect(pending);
    else if (active && active.resize) active.resize();
  }

  const rand = (a, b) => a + Math.random() * (b - a);
  const pick = (arr) => arr[(Math.random() * arr.length) | 0];
  // Smooth 1D value noise for wind gusts.
  const noiseSeed = Array.from({length: 256}, () => Math.random());
  function noise1(x) {
    const i = Math.floor(x), f = x - i, u = f * f * (3 - 2 * f);
    return noiseSeed[i & 255] * (1 - u) + noiseSeed[(i + 1) & 255] * u;
  }
  function makeCanvas(w, h) { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; }

  /* ====================== AUTUMN ====================== */
  const LEAF_PATHS = [
    // maple
    'M0,-48 L8,-30 L22,-38 L18,-18 L40,-22 L30,-6 L44,2 L22,8 L26,24 L8,16 L4,40 L0,46 L-4,40 L-8,16 L-26,24 L-22,8 L-44,2 L-30,-6 L-40,-22 L-18,-18 L-22,-38 L-8,-30 Z',
    // oak
    'M0,-46 C10,-42 6,-32 14,-30 C24,-28 18,-16 26,-12 C34,-8 22,2 28,8 C34,16 18,20 16,28 C14,36 6,40 0,46 C-6,40 -14,36 -16,28 C-18,20 -34,16 -28,8 C-22,2 -34,-8 -26,-12 C-18,-16 -24,-28 -14,-30 C-6,-32 -10,-42 0,-46 Z',
    // beech / birch
    'M0,-46 C24,-30 30,8 0,46 C-30,8 -24,-30 0,-46 Z',
  ];
  const LEAF_COLORS = [
    ['#f6b13a', '#d9601c'], ['#e8482b', '#9e1f1a'], ['#f2c84b', '#c98a1e'],
    ['#c9631f', '#7e2f12'], ['#ffa23a', '#e0571f'], ['#b8391f', '#6d1a10'],
  ];
  function leafSprite(pathIndex, colors, blur, back) {
    const S = 112, c = makeCanvas(S, S), g = c.getContext('2d');
    const draw = (target) => {
      target.save();
      target.translate(S / 2, S / 2);
      const p = new Path2D(LEAF_PATHS[pathIndex]);
      const grad = target.createLinearGradient(-40, -40, 40, 40);
      const [a, b] = colors;
      grad.addColorStop(0, back ? shade(a, -0.25) : a);
      grad.addColorStop(1, back ? shade(b, -0.25) : b);
      target.fillStyle = grad;
      target.fill(p);
      target.lineWidth = 1.6; target.strokeStyle = 'rgba(60,20,5,.45)'; target.stroke(p);
      // veins
      target.strokeStyle = back ? 'rgba(255,230,190,.18)' : 'rgba(80,25,5,.35)';
      target.lineWidth = 1.4;
      target.beginPath(); target.moveTo(0, 46); target.lineTo(0, -40); target.stroke();
      target.lineWidth = 0.9;
      for (const [y, dx] of [[20, 18], [4, 24], [-12, 20], [-26, 12]]) {
        target.beginPath(); target.moveTo(0, y); target.lineTo(dx, y - 12); target.moveTo(0, y); target.lineTo(-dx, y - 12); target.stroke();
      }
      // stem
      target.strokeStyle = shade(colors[1], -0.35); target.lineWidth = 2.2;
      target.beginPath(); target.moveTo(0, 44); target.lineTo(1, 56); target.stroke();
      target.restore();
    };
    if (blur > 0 && 'filter' in g) {
      const sharp = makeCanvas(S, S); draw(sharp.getContext('2d'));
      g.filter = `blur(${blur}px)`; g.drawImage(sharp, 0, 0);
    } else draw(g);
    return c;
  }
  function shade(hex, amt) {
    const n = parseInt(hex.slice(1), 16);
    let r = n >> 16, g = (n >> 8) & 255, b = n & 255;
    const f = amt < 0 ? 1 + amt : 1;
    r = Math.min(255, r * f + (amt > 0 ? (255 - r) * amt : 0));
    g = Math.min(255, g * f + (amt > 0 ? (255 - g) * amt : 0));
    b = Math.min(255, b * f + (amt > 0 ? (255 - b) * amt : 0));
    return `rgb(${r | 0},${g | 0},${b | 0})`;
  }

  function createAutumn() {
    // Pre-render every shape × palette × depth blur, front and back faces.
    const sprites = [];
    for (let p = 0; p < LEAF_PATHS.length; p++) for (const colors of LEAF_COLORS) {
      sprites.push({front: [0, 1.6, 3.4].map((b) => leafSprite(p, colors, b, false)),
                    back: [0, 1.6, 3.4].map((b) => leafSprite(p, colors, b, true))});
    }
    const leaves = [];
    const resting = [];
    function spawn(initial) {
      const z = Math.random() < 0.15 ? rand(1.15, 1.45) : rand(0.45, 1.05); // depth
      return {
        x: rand(-20, W + 20), y: initial ? rand(-H, H) : rand(-60, -20), z,
        size: (z > 1.1 ? rand(24, 34) : rand(11, 20)) * z,
        vy: rand(22, 42) * z,
        rot: rand(0, Math.PI * 2), vr: rand(-1.4, 1.4),
        flip: rand(0, Math.PI * 2), vf: rand(1.2, 3.2),
        swayA: rand(18, 42), swayF: rand(0.6, 1.4), swayO: rand(0, 10),
        sprite: pick(sprites),
        blur: z > 1.1 ? 2 : z < 0.65 ? 1 : 0,
        alpha: z > 1.1 ? 0.88 : z < 0.65 ? 0.7 : 0.95,
      };
    }
    function init() {
      leaves.length = 0;
      for (let i = 0; i < 22; i++) leaves.push(spawn(true));
      leaves.sort((a, b) => a.z - b.z);
      resting.length = 0;
      for (let i = 0; i < 7; i++) resting.push({x: rand(10, W - 10), rot: rand(-1, 1), size: rand(14, 22), sprite: pick(sprites), flipX: Math.random() < 0.5 ? -1 : 1});
    }
    init();
    let t = 0;
    return {
      resize: init,
      frame(dt) {
        t += dt;
        const gust = (noise1(t * 0.35) - 0.4) * 60 + Math.max(0, noise1(t * 0.9 + 40) - 0.7) * 160;
        ctx.clearRect(0, 0, W, H);
        for (const L of leaves) {
          L.y += L.vy * dt;
          L.x += (gust * L.z + Math.sin(t * L.swayF + L.swayO) * L.swayA) * dt;
          L.rot += L.vr * dt * (1 + Math.abs(gust) / 80);
          L.flip += L.vf * dt;
          if (L.y > H + 40 || L.x > W + 60 || L.x < -60) Object.assign(L, spawn(false));
          const sx = Math.cos(L.flip);
          const face = sx >= 0 ? L.sprite.front : L.sprite.back;
          const img = face[L.blur];
          const s = L.size / 56;
          ctx.save();
          ctx.globalAlpha = L.alpha * (0.75 + 0.25 * Math.abs(sx));
          ctx.translate(L.x, L.y);
          ctx.rotate(L.rot);
          ctx.scale(s * (Math.abs(sx) < 0.08 ? 0.08 * Math.sign(sx || 1) : sx), s);
          ctx.drawImage(img, -56, -56);
          ctx.restore();
        }
        // A few leaves settled on the bottom edge, stirring when the wind picks up.
        for (const R of resting) {
          const s = R.size / 56;
          ctx.save();
          ctx.globalAlpha = 0.9;
          ctx.translate(R.x, H - 6);
          ctx.rotate(Math.PI / 2 + R.rot + Math.sin(t * 2 + R.x) * Math.max(0, gust) * 0.002);
          ctx.scale(s * R.flipX, s * 0.55);
          ctx.drawImage(R.sprite.front[0], -56, -56);
          ctx.restore();
        }
      },
    };
  }

  /* ====================== WINTER ====================== */
  function createWinter() {
    const soft = makeCanvas(64, 64); {
      const g = soft.getContext('2d'); const gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
      gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(0.45, 'rgba(255,255,255,.85)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = gr; g.fillRect(0, 0, 64, 64);
    }
    const bokeh = makeCanvas(96, 96); {
      const g = bokeh.getContext('2d'); const gr = g.createRadialGradient(48, 48, 0, 48, 48, 48);
      gr.addColorStop(0, 'rgba(255,255,255,.55)'); gr.addColorStop(0.65, 'rgba(240,248,255,.35)'); gr.addColorStop(0.88, 'rgba(255,255,255,.5)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = gr; g.fillRect(0, 0, 96, 96);
    }
    const crystal = makeCanvas(64, 64); {
      const g = crystal.getContext('2d'); g.translate(32, 32); g.strokeStyle = 'rgba(255,255,255,.95)'; g.lineCap = 'round';
      for (let a = 0; a < 6; a++) {
        g.save(); g.rotate(a * Math.PI / 3);
        g.lineWidth = 2.2; g.beginPath(); g.moveTo(0, 0); g.lineTo(0, -26); g.stroke();
        g.lineWidth = 1.5;
        for (const [y, l] of [[-10, 7], [-18, 6], [-24, 3.5]]) { g.beginPath(); g.moveTo(0, y); g.lineTo(l, y - l); g.moveTo(0, y); g.lineTo(-l, y - l); g.stroke(); }
        g.restore();
      }
      g.fillStyle = '#fff'; g.beginPath(); g.arc(0, 0, 3, 0, Math.PI * 2); g.fill();
    }
    // Frost creeping in from the corners: fractal ice branches drawn once.
    let frost = null;
    function buildFrost() {
      frost = makeCanvas(Math.round(W * DPR), Math.round(H * DPR));
      const g = frost.getContext('2d'); g.scale(DPR, DPR);
      const corners = [[0, 0, 0.6], [W, 0, 2.2], [0, H, -0.6], [W, H, 3.8]];
      for (const [cx, cy, dir] of corners) {
        const gr = g.createRadialGradient(cx, cy, 0, cx, cy, 120);
        gr.addColorStop(0, 'rgba(225,240,255,.42)'); gr.addColorStop(1, 'rgba(225,240,255,0)');
        g.fillStyle = gr; g.fillRect(0, 0, W, H);
        g.strokeStyle = 'rgba(240,248,255,.55)'; g.lineCap = 'round';
        const branch = (x, y, ang, len, depth) => {
          if (depth === 0 || len < 3) return;
          const nx = x + Math.cos(ang) * len, ny = y + Math.sin(ang) * len;
          g.lineWidth = depth * 0.35; g.beginPath(); g.moveTo(x, y); g.lineTo(nx, ny); g.stroke();
          branch(nx, ny, ang + rand(-0.25, 0.25), len * 0.78, depth - 1);
          if (Math.random() < 0.75) branch(nx, ny, ang + rand(0.6, 1.1), len * 0.5, depth - 1);
          if (Math.random() < 0.75) branch(nx, ny, ang - rand(0.6, 1.1), len * 0.5, depth - 1);
        };
        for (let i = 0; i < 7; i++) branch(cx, cy, dir + rand(-0.75, 0.75), rand(16, 26), 5);
      }
    }
    const flakes = [];
    let pile = [], glints = [];
    const COLS = 85;
    function spawn(initial, layer) {
      const L = layer ?? (Math.random() < 0.55 ? 0 : Math.random() < 0.92 ? 1 : 2);
      const cfg = [
        {r: [1, 1.8], vy: [14, 24], a: 0.7, img: soft},
        {r: [2, 3.4], vy: [26, 42], a: 0.9, img: Math.random() < 0.3 ? crystal : soft},
        {r: [6, 10], vy: [46, 70], a: 0.32, img: bokeh},
      ][L];
      return {L, x: rand(0, W), y: initial ? rand(0, H) : rand(-30, -5), r: rand(...cfg.r), vy: rand(...cfg.vy), a: cfg.a, img: cfg.img,
              sw: rand(8, 22), sf: rand(0.5, 1.4), so: rand(0, 10), rot: rand(0, 6), vr: rand(-0.8, 0.8)};
    }
    function init() {
      flakes.length = 0;
      for (let i = 0; i < 120; i++) flakes.push(spawn(true));
      flakes.sort((a, b) => a.L - b.L);
      pile = new Array(COLS + 1).fill(0).map((_, i) => 3 + Math.sin(i * 0.4) * 1.5 + Math.random() * 1.5);
      glints = Array.from({length: 14}, () => ({i: (Math.random() * COLS) | 0, p: rand(0, 6), s: rand(1.5, 3.5)}));
      buildFrost();
    }
    init();
    let t = 0;
    return {
      resize: init,
      frame(dt) {
        t += dt;
        const wind = (noise1(t * 0.25) - 0.5) * 30;
        ctx.clearRect(0, 0, W, H);
        ctx.drawImage(frost, 0, 0, W, H);
        for (const f of flakes) {
          f.y += f.vy * dt;
          f.x += (wind * (0.5 + f.L * 0.4) + Math.sin(t * f.sf + f.so) * f.sw) * dt;
          f.rot += f.vr * dt;
          if (f.x < -20) f.x += W + 40; if (f.x > W + 20) f.x -= W + 40;
          const col = Math.max(0, Math.min(COLS, Math.round((f.x / W) * COLS)));
          const ground = H - pile[col];
          if (f.L < 2 && f.y >= ground) {
            // Settle: grow the drift a little and smooth it into the neighbours.
            if (pile[col] < 22) {
              pile[col] += 0.35;
              if (col > 0) pile[col - 1] += 0.15;
              if (col < COLS) pile[col + 1] += 0.15;
            }
            Object.assign(f, spawn(false, f.L));
            continue;
          }
          if (f.y > H + 20) { Object.assign(f, spawn(false, f.L)); continue; }
          const d = f.r * 2;
          ctx.globalAlpha = f.a;
          if (f.img === crystal) {
            ctx.save(); ctx.translate(f.x, f.y); ctx.rotate(f.rot); ctx.drawImage(crystal, -d * 1.6, -d * 1.6, d * 3.2, d * 3.2); ctx.restore();
          } else ctx.drawImage(f.img, f.x - d, f.y - d, d * 2, d * 2);
        }
        ctx.globalAlpha = 1;
        // Snow drift along the bottom edge.
        for (let i = 1; i < COLS; i++) pile[i] = pile[i] * 0.96 + (pile[i - 1] + pile[i + 1]) * 0.02;
        const grad = ctx.createLinearGradient(0, H - 26, 0, H);
        grad.addColorStop(0, '#ffffff'); grad.addColorStop(1, '#cfdcf3');
        ctx.beginPath(); ctx.moveTo(0, H);
        for (let i = 0; i <= COLS; i++) ctx.lineTo((i / COLS) * W, H - pile[i]);
        ctx.lineTo(W, H); ctx.closePath();
        ctx.shadowColor = 'rgba(120,160,220,.55)'; ctx.shadowBlur = 8; ctx.shadowOffsetY = -2;
        ctx.fillStyle = grad; ctx.fill();
        ctx.shadowColor = 'transparent';
        // Sparkling glints on the snow surface.
        for (const g of glints) {
          const a = Math.max(0, Math.sin(t * g.s + g.p)) ** 6;
          if (a < 0.02) continue;
          const x = (g.i / COLS) * W, y = H - pile[g.i] + 3;
          ctx.globalAlpha = a; ctx.fillStyle = '#fff';
          ctx.fillRect(x - 0.5, y - 4, 1, 8); ctx.fillRect(x - 4, y - 0.5, 8, 1);
        }
        ctx.globalAlpha = 1;
      },
    };
  }

  /* ====================== FIRE ====================== */
  const FIRE_FRAG = `
    precision highp float;
    uniform vec2 res; uniform float t;
    float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453); }
    float noise(vec2 p){ vec2 i=floor(p), f=fract(p); vec2 u=f*f*(3.-2.*f);
      return mix(mix(hash(i),hash(i+vec2(1,0)),u.x), mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),u.x), u.y); }
    float fbm(vec2 p){ float v=0., a=.5; for(int i=0;i<5;i++){ v+=a*noise(p); p*=2.03; a*=.5; } return v; }
    void main(){
      vec2 uv = gl_FragCoord.xy / res;
      float aspect = res.x / res.y;
      vec2 q = vec2(uv.x * aspect * 3.2, uv.y * 3.0);
      float warp = fbm(q * 1.4 - vec2(0., t * 0.9));
      float n = fbm(vec2(q.x + warp * 0.6, q.y - t * 1.7));
      // Flame sheet rising from the bottom edge.
      float h = 0.15 + 0.05 * sin(uv.x * 7. + t * 1.3) + 0.04 * sin(uv.x * 17. - t * 2.1);
      float bottom = smoothstep(h, 0.0, uv.y - (n - 0.5) * 0.22);
      // Keep the centre (where the profile actions sit) lower and calmer than the corners.
      bottom *= mix(0.45, 1.0, smoothstep(0.18, 0.42, abs(uv.x - 0.5)));
      // Thinner flames licking up the side edges.
      float side = min(uv.x, 1. - uv.x);
      float sides = smoothstep(0.1, 0.0, side - (n - 0.5) * 0.09) * smoothstep(0.75, 0.05, uv.y) * 0.75;
      float f = clamp(max(bottom, sides) * (0.75 + n * 0.6), 0., 1.);
      vec3 col = mix(vec3(0.12, 0.0, 0.0), vec3(0.95, 0.18, 0.02), smoothstep(0.05, 0.38, f));
      col = mix(col, vec3(1.0, 0.55, 0.08), smoothstep(0.38, 0.68, f));
      col = mix(col, vec3(1.0, 0.92, 0.62), smoothstep(0.7, 1.0, f));
      float alpha = smoothstep(0.04, 0.55, f);
      gl_FragColor = vec4(col * alpha, alpha);
    }`;
  function createFire() {
    const gl = getGL();
    let prog = null, uRes = null, uT = null;
    if (gl) {
      const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return s; };
      prog = gl.createProgram();
      gl.attachShader(prog, sh(gl.VERTEX_SHADER, 'attribute vec2 p; void main(){ gl_Position = vec4(p, 0., 1.); }'));
      gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FIRE_FRAG));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) prog = null;
      if (prog) {
        gl.useProgram(prog);
        const b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
        const loc = gl.getAttribLocation(prog, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
        uRes = gl.getUniformLocation(prog, 'res'); uT = gl.getUniformLocation(prog, 't');
      }
    }
    const glow = makeCanvas(32, 32); {
      const g = glow.getContext('2d'); const gr = g.createRadialGradient(16, 16, 0, 16, 16, 16);
      gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(0.25, 'rgba(255,255,255,.9)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = gr; g.fillRect(0, 0, 32, 32);
    }
    const embers = [];
    function spawnEmber(burst) {
      const fromSide = Math.random() < 0.25;
      return {
        x: fromSide ? (Math.random() < 0.5 ? rand(0, 14) : rand(W - 14, W)) : rand(0, W),
        y: fromSide ? rand(H * 0.4, H) : rand(H - 30, H + 5),
        vx: rand(-14, 14), vy: -rand(40, burst ? 160 : 110),
        life: 0, max: rand(1.2, 2.8), size: rand(0.9, 2.4), ph: rand(0, 6), swirl: rand(10, 30),
      };
    }
    for (let i = 0; i < 60; i++) { const e = spawnEmber(false); e.life = rand(0, e.max); embers.push(e); }
    let t = 0, nextBurst = 1.5;
    return {
      stop() { if (gl) { gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT); } },
      frame(dt) {
        t += dt;
        if (gl && prog) {
          gl.viewport(0, 0, glCanvas.width, glCanvas.height);
          gl.uniform2f(uRes, glCanvas.width, glCanvas.height); gl.uniform1f(uT, t);
          gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
          gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
        ctx.clearRect(0, 0, W, H);
        ctx.globalCompositeOperation = 'lighter';
        nextBurst -= dt;
        if (nextBurst <= 0) { for (let i = 0; i < 18; i++) embers.push(spawnEmber(true)); nextBurst = rand(1.8, 3.6); }
        for (let i = embers.length - 1; i >= 0; i--) {
          const e = embers[i];
          e.life += dt;
          if (e.life >= e.max) { if (embers.length > 70) embers.splice(i, 1); else Object.assign(e, spawnEmber(false)); continue; }
          e.vy += 8 * dt; // drag slows the climb
          e.x += (e.vx + Math.sin(t * 3 + e.ph) * e.swirl) * dt;
          e.y += e.vy * dt;
          const k = e.life / e.max;
          const flicker = 0.6 + 0.4 * Math.sin(t * 22 + e.ph * 7);
          const r = 255, g = Math.round(220 - 150 * k), b = Math.round(120 - 110 * k);
          ctx.globalAlpha = (1 - k) * flicker;
          const s = e.size * (5 + 3 * (1 - k));
          ctx.drawImage(tinted(glow, r, g, b), e.x - s, e.y - s, s * 2, s * 2);
        }
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
      },
    };
  }
  // Cache a handful of tinted glow sprites (quantised colour) to avoid per-frame recolouring.
  const tintCache = new Map();
  function tinted(img, r, g, b) {
    const key = `${r >> 4}-${g >> 4}-${b >> 4}`;
    let c = tintCache.get(key);
    if (!c) {
      c = makeCanvas(img.width, img.height); const x = c.getContext('2d');
      x.drawImage(img, 0, 0); x.globalCompositeOperation = 'source-in';
      x.fillStyle = `rgb(${r},${g},${b})`; x.fillRect(0, 0, c.width, c.height);
      tintCache.set(key, c);
    }
    return c;
  }

  /* ====================== shared helpers for the new effects ====================== */
  const GL_NOISE = `
    precision highp float;
    uniform vec2 res; uniform float t; uniform float flash;
    float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453); }
    float noise(vec2 p){ vec2 i=floor(p), f=fract(p); vec2 u=f*f*(3.-2.*f);
      return mix(mix(hash(i),hash(i+vec2(1,0)),u.x), mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),u.x), u.y); }
    float fbm(vec2 p){ float v=0., a=.5; for(int i=0;i<5;i++){ v+=a*noise(p); p*=2.03; a*=.5; } return v; }`;
  // Full-card fragment shader on the shared GL canvas (screen-blended over the card).
  function glLayer(body) {
    const gl = getGL();
    if (!gl) return {draw() {}, clear() {}};
    const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return s; };
    const prog = gl.createProgram();
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, 'attribute vec2 p; void main(){ gl_Position = vec4(p, 0., 1.); }'));
    gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, GL_NOISE + body));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return {draw() {}, clear() {}};
    gl.useProgram(prog);
    const b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const uRes = gl.getUniformLocation(prog, 'res'), uT = gl.getUniformLocation(prog, 't'), uF = gl.getUniformLocation(prog, 'flash');
    return {
      draw(time, flash = 0) {
        gl.viewport(0, 0, glCanvas.width, glCanvas.height);
        gl.uniform2f(uRes, glCanvas.width, glCanvas.height); gl.uniform1f(uT, time); if (uF) gl.uniform1f(uF, flash);
        gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      },
      clear() { gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT); },
    };
  }
  function glowSprite(size, stops) {
    const c = makeCanvas(size, size), g = c.getContext('2d'), h = size / 2;
    const gr = g.createRadialGradient(h, h, 0, h, h, h);
    for (const [o, col] of stops) gr.addColorStop(o, col);
    g.fillStyle = gr; g.fillRect(0, 0, size, size); return c;
  }
  // Starfield shared by aurora / galaxy.
  function makeStars(n) {
    return Array.from({length: n}, () => ({x: Math.random(), y: Math.random(), r: rand(0.4, 1.4), p: rand(0, 6), s: rand(0.8, 3), z: rand(0.2, 1)}));
  }
  function drawStars(stars, t, maxY = 1, drift = 0) {
    ctx.fillStyle = '#fff';
    for (const s of stars) {
      if (s.y > maxY) continue;
      const a = 0.35 + 0.65 * Math.max(0, Math.sin(t * s.s + s.p)) ** 2;
      const x = ((s.x + drift * s.z * t) % 1 + 1) % 1;
      ctx.globalAlpha = a; ctx.beginPath(); ctx.arc(x * W, s.y * H, s.r, 0, 6.283); ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  /* ====================== SAKURA ====================== */
  function createSakura() {
    const petal = (blur, tone) => {
      const S = 64, c = makeCanvas(S, S), g = c.getContext('2d');
      const paint = (x) => {
        x.translate(S / 2, S / 2);
        const p = new Path2D('M0,22 C-18,10 -20,-12 -7,-22 C-3,-25 0,-20 0,-17 C0,-20 3,-25 7,-22 C20,-12 18,10 0,22 Z');
        const gr = x.createRadialGradient(0, 14, 2, 0, 0, 26);
        gr.addColorStop(0, tone ? '#ffd9e6' : '#fff1f5'); gr.addColorStop(0.6, tone ? '#f7a3c0' : '#ffc4d8'); gr.addColorStop(1, tone ? '#e77fa6' : '#f59bbd');
        x.fillStyle = gr; x.fill(p);
        x.strokeStyle = 'rgba(200,80,120,.25)'; x.lineWidth = 0.8; x.beginPath(); x.moveTo(0, 18); x.lineTo(0, -12); x.stroke();
      };
      if (blur && 'filter' in g) { const s = makeCanvas(S, S); paint(s.getContext('2d')); g.filter = `blur(${blur}px)`; g.drawImage(s, 0, 0); }
      else paint(g);
      return c;
    };
    const sprites = [petal(0, 0), petal(0, 1), petal(1.5, 0), petal(2.8, 1)];
    // Blossoming branch reaching in from the top-right corner, drawn once.
    let branch = null;
    function buildBranch() {
      branch = makeCanvas(Math.round(W * DPR), Math.round(H * DPR));
      const g = branch.getContext('2d'); g.scale(DPR, DPR);
      g.strokeStyle = '#3a2420'; g.lineCap = 'round';
      const limb = (x, y, a, len, w, d) => {
        if (d === 0) return;
        const nx = x + Math.cos(a) * len, ny = y + Math.sin(a) * len;
        g.lineWidth = w; g.beginPath(); g.moveTo(x, y); g.quadraticCurveTo((x + nx) / 2 + rand(-6, 6), (y + ny) / 2 + rand(-6, 6), nx, ny); g.stroke();
        for (let i = 0; i < d + 1; i++) {
          const k = Math.random(), bx = x + (nx - x) * k, by = y + (ny - y) * k;
          for (let j = 0; j < 5; j++) {
            g.save(); g.translate(bx + rand(-4, 4), by + rand(-4, 4)); g.rotate(j * 1.2566 + rand(0, .3));
            g.drawImage(sprites[j % 2], -4, -9, 8, 9); g.restore();
          }
          g.fillStyle = '#ffe9a8'; g.beginPath(); g.arc(bx, by, 1.2, 0, 6.283); g.fill();
        }
        limb(nx, ny, a + rand(-0.5, 0.2), len * 0.72, w * 0.62, d - 1);
        if (Math.random() < 0.8) limb(nx, ny, a + rand(0.4, 0.9), len * 0.55, w * 0.55, d - 1);
      };
      limb(W + 8, -6, 2.45, 46, 5, 4);
    }
    const petals = [];
    const spawn = (initial) => {
      const z = rand(0.5, 1.3);
      return {x: rand(W * 0.2, W + 40), y: initial ? rand(-20, H) : rand(-30, 10), z, size: rand(9, 15) * z, vx: -rand(14, 30) * z, vy: rand(18, 34) * z,
              rot: rand(0, 6), vr: rand(-2, 2), flip: rand(0, 6), vf: rand(2, 4.5), sw: rand(10, 26), sf: rand(.8, 1.6), so: rand(0, 9),
              img: sprites[z > 1.1 ? 3 : z < .7 ? 2 : (Math.random() * 2) | 0], a: z > 1.1 ? .8 : .95};
    };
    function init() { petals.length = 0; for (let i = 0; i < 34; i++) petals.push(spawn(true)); petals.sort((a, b) => a.z - b.z); buildBranch(); }
    init();
    let t = 0;
    const bloom = glowSprite(256, [[0, 'rgba(255,200,225,.35)'], [1, 'rgba(255,200,225,0)']]);
    return {
      resize: init,
      frame(dt) {
        t += dt; ctx.clearRect(0, 0, W, H);
        ctx.drawImage(bloom, W * 0.35, -H * 0.25, W, W);
        const breeze = (noise1(t * 0.4) - 0.4) * 40;
        for (const p of petals) {
          p.x += (p.vx - breeze * p.z + Math.sin(t * p.sf + p.so) * p.sw) * dt; p.y += p.vy * dt;
          p.rot += p.vr * dt; p.flip += p.vf * dt;
          if (p.y > H + 20 || p.x < -30) Object.assign(p, spawn(false));
          const sx = Math.cos(p.flip), s = p.size / 32;
          ctx.save(); ctx.globalAlpha = p.a * (0.7 + 0.3 * Math.abs(sx)); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
          ctx.scale(s * (Math.abs(sx) < 0.1 ? 0.1 : sx), s); ctx.drawImage(p.img, -32, -32); ctx.restore();
        }
        ctx.drawImage(branch, 0, 0, W, H);
      },
    };
  }

  /* ====================== AURORA ====================== */
  const AURORA = `
    void main(){
      vec2 uv = gl_FragCoord.xy / res;
      float y = 1.0 - uv.y;
      vec3 acc = vec3(0.);
      for (int i = 0; i < 3; i++) {
        float fi = float(i);
        float wave = 0.18 + 0.1 * fi + 0.06 * sin(uv.x * (3.0 + fi) + t * (0.35 + fi * 0.1)) + 0.05 * fbm(vec2(uv.x * 2.5 + fi, t * 0.15));
        float d = y - wave;
        float curtain = exp(-d * d * 90.0) + smoothstep(0.0, -0.22, d) * 0.35 * exp(d * 6.0);
        float streak = 0.55 + 0.45 * noise(vec2(uv.x * 60.0 + fi * 13.0, t * 0.6));
        vec3 col = fi < 0.5 ? vec3(0.15, 1.0, 0.55) : fi < 1.5 ? vec3(0.2, 0.75, 1.0) : vec3(0.75, 0.35, 1.0);
        acc += col * curtain * streak * (0.55 - fi * 0.1);
      }
      acc *= smoothstep(0.85, 0.35, y);
      float a = clamp(max(acc.r, max(acc.g, acc.b)), 0.0, 1.0);
      gl_FragColor = vec4(acc, a);
    }`;
  function createAurora() {
    const layer = glLayer(AURORA), stars = makeStars(70);
    let t = 0;
    return {
      stop() { layer.clear(); },
      frame(dt) { t += dt; layer.draw(t); ctx.clearRect(0, 0, W, H); drawStars(stars, t, 0.6); },
    };
  }

  /* ====================== RAIN ON GLASS ====================== */
  function createRain() {
    const beads = [], runners = [], streaks = [];
    function bead(x, y, r) { return {x, y, r}; }
    function init() {
      beads.length = 0; runners.length = 0; streaks.length = 0;
      for (let i = 0; i < 170; i++) beads.push(bead(rand(0, W), rand(0, H), Math.random() < 0.15 ? rand(2.8, 4.2) : rand(1, 2.6)));
      for (let i = 0; i < 9; i++) runners.push({x: rand(10, W - 10), y: rand(-H, H * 0.5), r: rand(3.5, 5.5), v: rand(40, 90), trail: []});
      for (let i = 0; i < 70; i++) streaks.push({x: rand(0, W), y: rand(0, H), l: rand(10, 22), v: rand(420, 620)});
    }
    init();
    // One pre-rendered drop, scaled per bead. Building a radial gradient per drop per frame
    // (~700 a frame with trails) churned hundreds of MB and spiked memory past 1 GB.
    const DROP_R = 16;
    const dropSprite = (() => {
      const c = makeCanvas(DROP_R * 2, Math.ceil(DROP_R * 2.24)), x = c.getContext('2d'), cx = DROP_R, cy = DROP_R * 1.12;
      const g = x.createRadialGradient(cx - DROP_R * 0.3, cy - DROP_R * 0.4, DROP_R * 0.1, cx, cy, DROP_R);
      g.addColorStop(0, 'rgba(255,255,255,.95)'); g.addColorStop(0.3, 'rgba(220,235,255,.4)'); g.addColorStop(0.8, 'rgba(10,20,40,.55)'); g.addColorStop(1, 'rgba(255,255,255,.6)');
      x.fillStyle = g; x.beginPath(); x.ellipse(cx, cy, DROP_R, DROP_R * 1.12, 0, 0, 6.283); x.fill();
      return c;
    })();
    function drawDrop(x, y, r) {
      ctx.drawImage(dropSprite, x - r, y - r * 1.12, r * 2, r * 2.24);
    }
    let t = 0;
    return {
      resize: init,
      frame(dt) {
        t += dt; ctx.clearRect(0, 0, W, H);
        // Distant rain behind the glass.
        ctx.strokeStyle = 'rgba(190,210,240,.18)'; ctx.lineWidth = 1;
        ctx.beginPath();
        for (const s of streaks) {
          s.y += s.v * dt; s.x -= s.v * 0.12 * dt; if (s.y > H) { s.y = -s.l; s.x = rand(0, W + 40); }
          ctx.moveTo(s.x, s.y); ctx.lineTo(s.x + s.l * 0.12, s.y - s.l);
        }
        ctx.stroke();
        for (const b of beads) drawDrop(b.x, b.y, b.r);
        // Heavy drops sliding down, wobbling and eating beads in their path.
        for (const d of runners) {
          d.y += d.v * dt * (0.6 + 0.4 * noise1(t * 2 + d.x)); d.x += Math.sin(t * 3 + d.r * 7) * 6 * dt;
          if (d.trail.length < 40 && Math.random() < 0.25) d.trail.push({x: d.x + rand(-1, 1), y: d.y - d.r, r: rand(0.6, 1.3), life: 1});
          for (const b of beads) if (Math.abs(b.x - d.x) < d.r + b.r && Math.abs(b.y - d.y) < d.r + b.r) { b.x = rand(0, W); b.y = rand(0, H); d.r = Math.min(6.5, d.r + 0.05); }
          for (const tr of d.trail) { tr.life -= dt * 0.25; if (tr.life > 0) { ctx.globalAlpha = tr.life; drawDrop(tr.x, tr.y, tr.r); } }
          ctx.globalAlpha = 1;
          d.trail = d.trail.filter((tr) => tr.life > 0);
          drawDrop(d.x, d.y, d.r);
          if (d.y > H + 10) Object.assign(d, {x: rand(10, W - 10), y: rand(-60, -10), r: rand(3, 5), v: rand(40, 90), trail: []});
        }
      },
    };
  }

  /* ====================== GALAXY ====================== */
  const NEBULA = `
    void main(){
      vec2 uv = gl_FragCoord.xy / res;
      vec2 p = vec2(uv.x * res.x / res.y, uv.y) * 2.2;
      float n = fbm(p + vec2(t * 0.03, -t * 0.02) + fbm(p * 1.7 - t * 0.04));
      float n2 = fbm(p * 2.3 - vec2(t * 0.05, 0.));
      vec3 col = mix(vec3(0.25, 0.05, 0.45), vec3(0.1, 0.35, 0.9), smoothstep(0.35, 0.75, n));
      col = mix(col, vec3(1.0, 0.35, 0.65), smoothstep(0.62, 0.85, n2) * 0.7);
      float edge = 1.0 - smoothstep(0.08, 0.42, min(min(uv.x, 1.0 - uv.x), min(uv.y, 1.0 - uv.y)) * 1.0);
      float a = (smoothstep(0.38, 0.85, n) * 0.55 + smoothstep(0.6, 0.9, n2) * 0.25) * mix(0.35, 1.0, edge);
      gl_FragColor = vec4(col * a, a);
    }`;
  function createGalaxy() {
    const layer = glLayer(NEBULA), stars = makeStars(120);
    const glow = glowSprite(32, [[0, 'rgba(255,255,255,1)'], [0.3, 'rgba(200,220,255,.8)'], [1, 'rgba(200,220,255,0)']]);
    let t = 0, shooter = null, next = 1.2;
    return {
      stop() { layer.clear(); },
      frame(dt) {
        t += dt; layer.draw(t); ctx.clearRect(0, 0, W, H);
        drawStars(stars, t, 1, 0.004);
        for (const s of stars.slice(0, 8)) { const a = 0.5 + 0.5 * Math.sin(t * s.s + s.p); ctx.globalAlpha = a; ctx.drawImage(glow, s.x * W - 7, s.y * H - 7, 14, 14); }
        ctx.globalAlpha = 1;
        next -= dt;
        if (!shooter && next <= 0) shooter = {x: rand(W * 0.3, W), y: rand(0, H * 0.4), vx: -rand(260, 360), vy: rand(90, 140), life: 0};
        if (shooter) {
          shooter.life += dt; shooter.x += shooter.vx * dt; shooter.y += shooter.vy * dt;
          const g = ctx.createLinearGradient(shooter.x, shooter.y, shooter.x - shooter.vx * 0.18, shooter.y - shooter.vy * 0.18);
          g.addColorStop(0, 'rgba(255,255,255,.95)'); g.addColorStop(1, 'rgba(160,190,255,0)');
          ctx.strokeStyle = g; ctx.lineWidth = 1.8; ctx.beginPath(); ctx.moveTo(shooter.x, shooter.y); ctx.lineTo(shooter.x - shooter.vx * 0.18, shooter.y - shooter.vy * 0.18); ctx.stroke();
          ctx.drawImage(glow, shooter.x - 6, shooter.y - 6, 12, 12);
          if (shooter.x < -60 || shooter.y > H + 40) { shooter = null; next = rand(2.5, 5); }
        }
      },
    };
  }

  /* ====================== NEON CYBER ====================== */
  function createNeon() {
    let t = 0, glitch = 0, nextGlitch = 1.5;
    return {
      frame(dt) {
        t += dt; ctx.clearRect(0, 0, W, H);
        // Perspective grid racing toward the viewer along the bottom.
        const horizon = H * 0.8, vx = W / 2;
        ctx.save(); ctx.beginPath(); ctx.rect(0, horizon, W, H - horizon); ctx.clip();
        const fade = ctx.createLinearGradient(0, horizon, 0, H);
        fade.addColorStop(0, 'rgba(255,40,200,0)'); fade.addColorStop(1, 'rgba(255,40,200,.75)');
        ctx.strokeStyle = fade; ctx.lineWidth = 1.2; ctx.shadowColor = '#ff2bd6'; ctx.shadowBlur = 8;
        ctx.beginPath();
        for (let i = -10; i <= 10; i++) { ctx.moveTo(vx + i * 6, horizon); ctx.lineTo(vx + i * 60, H); }
        const off = (t * 0.6) % 1;
        for (let k = 0; k < 9; k++) { const z = (k + off) / 9, y = horizon + (H - horizon) * z * z; ctx.moveTo(0, y); ctx.lineTo(W, y); }
        ctx.stroke(); ctx.restore();
        // Horizon glow line.
        ctx.fillStyle = 'rgba(0,240,255,.8)'; ctx.shadowColor = '#00f0ff'; ctx.shadowBlur = 14; ctx.fillRect(0, horizon - 1, W, 2); ctx.shadowBlur = 0;
        // Scanlines.
        ctx.fillStyle = 'rgba(0,0,0,.18)'; for (let y = (t * 30) % 3; y < H; y += 3) ctx.fillRect(0, y, W, 1);
        // Occasional glitch bursts: RGB-split bars and data blocks.
        nextGlitch -= dt; if (nextGlitch <= 0) { glitch = 0.22; nextGlitch = rand(1.6, 3.4); }
        if (glitch > 0) {
          glitch -= dt;
          for (let i = 0; i < 6; i++) {
            const y = rand(0, H), h = rand(2, 9), o = rand(-14, 14);
            ctx.fillStyle = 'rgba(0,255,255,.35)'; ctx.fillRect(o, y, W, h);
            ctx.fillStyle = 'rgba(255,0,170,.35)'; ctx.fillRect(-o, y + 2, W, h * 0.6);
          }
          for (let i = 0; i < 10; i++) { ctx.fillStyle = Math.random() < .5 ? 'rgba(0,255,200,.7)' : 'rgba(255,255,255,.6)'; ctx.fillRect(rand(0, W), rand(0, H), rand(4, 18), rand(2, 5)); }
        }
      },
    };
  }

  /* ====================== STORM ====================== */
  const CLOUDS = `
    void main(){
      vec2 uv = gl_FragCoord.xy / res;
      vec2 p = vec2(uv.x * res.x / res.y * 2.0, uv.y * 2.0);
      float n = fbm(p + vec2(t * 0.08, 0.) + fbm(p * 1.5 + t * 0.05));
      float top = smoothstep(0.35, 0.95, uv.y);
      vec3 base = mix(vec3(0.12, 0.14, 0.2), vec3(0.42, 0.46, 0.58), n);
      vec3 col = base + flash * vec3(0.75, 0.8, 1.0) * n;
      float a = top * smoothstep(0.3, 0.75, n) * 0.85 + flash * 0.25 * top;
      gl_FragColor = vec4(col * a, a);
    }`;
  function createStorm() {
    const layer = glLayer(CLOUDS), drops = [];
    function init() { drops.length = 0; for (let i = 0; i < 90; i++) drops.push({x: rand(0, W), y: rand(0, H), l: rand(10, 18), v: rand(520, 720)}); }
    init();
    let t = 0, flash = 0, bolt = null, next = 1.4;
    function makeBolt() {
      let pts = [[rand(W * 0.2, W * 0.8), 0], [rand(W * 0.2, W * 0.8), H * rand(0.55, 0.8)]];
      for (let d = 0; d < 6; d++) {
        const out = [pts[0]];
        for (let i = 1; i < pts.length; i++) {
          const [ax, ay] = pts[i - 1], [bx, by] = pts[i];
          out.push([(ax + bx) / 2 + rand(-1, 1) * 30 / (d + 1), (ay + by) / 2], [bx, by]);
        }
        pts = out;
      }
      const branches = [];
      for (let i = 0; i < 3; i++) { const s = pts[(rand(10, pts.length - 10)) | 0]; branches.push([s, [s[0] + rand(-40, 40), s[1] + rand(30, 70)]]); }
      return {pts, branches, life: 0.32};
    }
    return {
      resize: init,
      stop() { layer.clear(); },
      frame(dt) {
        t += dt; next -= dt;
        if (next <= 0) { bolt = makeBolt(); flash = 1; next = rand(2.2, 4.5); }
        flash = Math.max(0, flash - dt * 3.2);
        layer.draw(t, flash);
        ctx.clearRect(0, 0, W, H);
        if (flash > 0) { ctx.fillStyle = `rgba(200,215,255,${flash * 0.18})`; ctx.fillRect(0, 0, W, H); }
        ctx.strokeStyle = 'rgba(180,200,235,.35)'; ctx.lineWidth = 1; ctx.beginPath();
        for (const d of drops) { d.y += d.v * dt; d.x -= d.v * 0.18 * dt; if (d.y > H) { d.y = -d.l; d.x = rand(0, W + 60); } ctx.moveTo(d.x, d.y); ctx.lineTo(d.x + d.l * 0.18, d.y - d.l); }
        ctx.stroke();
        if (bolt) {
          bolt.life -= dt;
          if (bolt.life <= 0) bolt = null;
          else {
            const a = Math.min(1, bolt.life * 5) * (0.7 + 0.3 * Math.random());
            ctx.save(); ctx.globalAlpha = a; ctx.strokeStyle = '#eef3ff'; ctx.shadowColor = '#9fc0ff'; ctx.shadowBlur = 18; ctx.lineJoin = 'round';
            ctx.lineWidth = 2.2; ctx.beginPath(); ctx.moveTo(...bolt.pts[0]); for (const p of bolt.pts) ctx.lineTo(...p); ctx.stroke();
            ctx.lineWidth = 1; for (const [a1, b1] of bolt.branches) { ctx.beginPath(); ctx.moveTo(...a1); ctx.lineTo(...b1); ctx.stroke(); }
            ctx.restore();
          }
        }
      },
    };
  }

  /* ====================== FIREFLIES ====================== */
  function createFireflies() {
    const glow = glowSprite(64, [[0, 'rgba(255,255,220,1)'], [0.12, 'rgba(230,255,120,.95)'], [0.4, 'rgba(180,255,80,.25)'], [1, 'rgba(160,255,60,0)']]);
    const flies = []; let grass = null;
    function buildGrass() {
      grass = makeCanvas(Math.round(W * DPR), Math.round(H * DPR));
      const g = grass.getContext('2d'); g.scale(DPR, DPR);
      for (let i = 0; i < 160; i++) {
        const x = rand(-5, W + 5), h = Math.random() < 0.2 ? rand(16, 26) : rand(6, 15), lean = rand(-8, 8);
        g.strokeStyle = `rgba(${10 + rand(0, 15)},${25 + rand(0, 20)},${18 + rand(0, 10)},.95)`; g.lineWidth = rand(1, 2.4);
        g.beginPath(); g.moveTo(x, H + 2); g.quadraticCurveTo(x + lean * 0.3, H - h * 0.6, x + lean, H - h); g.stroke();
      }
    }
    function init() {
      flies.length = 0;
      for (let i = 0; i < 26; i++) flies.push({x: rand(0, W), y: rand(H * 0.25, H - 20), z: rand(0.5, 1.4), ph: rand(0, 50), pulse: rand(0.6, 1.4), po: rand(0, 6)});
      buildGrass();
    }
    init();
    let t = 0;
    const dusk = ctx.createLinearGradient ? null : null;
    return {
      resize: init,
      frame(dt) {
        t += dt; ctx.clearRect(0, 0, W, H);
        const g = ctx.createLinearGradient(0, H * 0.45, 0, H);
        g.addColorStop(0, 'rgba(20,40,60,0)'); g.addColorStop(1, 'rgba(10,30,30,.22)');
        ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
        ctx.globalCompositeOperation = 'lighter';
        for (const f of flies) {
          // Wandering flight from layered noise.
          f.x += (noise1(t * 0.35 + f.ph) - 0.5) * 60 * f.z * dt;
          f.y += (noise1(t * 0.3 + f.ph + 100) - 0.5) * 46 * f.z * dt;
          if (f.x < -10) f.x = W + 10; if (f.x > W + 10) f.x = -10;
          f.y = Math.max(H * 0.15, Math.min(H - 12, f.y));
          const blink = Math.max(0, Math.sin(t * f.pulse + f.po)) ** 3;
          const s = 18 * f.z * (0.6 + 0.4 * blink);
          ctx.globalAlpha = 0.15 + 0.85 * blink;
          ctx.drawImage(glow, f.x - s, f.y - s, s * 2, s * 2);
        }
        ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = 1;
        ctx.drawImage(grass, 0, 0, W, H);
      },
    };
  }

  /* ====================== runner ====================== */
  const factories = {autumn: createAutumn, winter: createWinter, fire: createFire, sakura: createSakura, aurora: createAurora, rain: createRain, galaxy: createGalaxy, neon: createNeon, storm: createStorm, fireflies: createFireflies};
  let active = null, pending = null;
  function setEffect(name) {
    if (W < 2 || H < 2) { pending = name; return; }
    pending = null;
    if (active && active.stop) active.stop();
    glCanvas.style.mixBlendMode = name === 'storm' ? 'normal' : 'screen';
    ctx.clearRect(0, 0, W, H);
    active = factories[name] ? factories[name]() : null;
  }
  return {
    resize,
    setEffect,
    frame(dt) { if (active) active.frame(dt); },
    dispose() {
      if (active && active.stop) active.stop();
      active = null; pending = null;
      if (glContext) { const ext = glContext.getExtension('WEBGL_lose_context'); if (ext) ext.loseContext(); glContext = null; }
      // Drop the backing stores now instead of waiting for GC.
      for (const c of [canvas, glCanvas]) { c.width = 0; c.height = 0; }
    },
  };
}
